import { addDays, eachDayOfInterval, format, isWeekend, parseISO, startOfDay, startOfWeek } from 'date-fns'

import { getDate } from '../../../app/header/utils'
import { ISO_DATEONLY_FORMAT } from './constants'
import {
  DividendsEntry,
  DividendsEntryRaw,
  EarningsEntry,
  EarningsEntryRaw,
  EconomicAlertEntry,
  EconomicCalendarData,
  EconomicEntry,
  PastNextFuture,
  RawEconomicEntry,
} from './types'

export function processDetailTableData(rawData: RawEconomicEntry[]): EconomicEntry[] {
  if (!rawData) {
    return []
  }
  return rawData.map((rawItem) => ({ ...rawItem, parsedDate: parseISO(rawItem.date) }) as EconomicEntry)
}

export function processEconomicCalendarData(
  rawData: RawEconomicEntry[],
  now: Date,
  dateFrom: Date,
  openedDetail: number | null,
  alertsState: Record<string, EconomicAlertEntry | null> | null
): EconomicCalendarData {
  if (!rawData) {
    return { days: [] }
  }

  let alreadyFuture = false
  const eventsByDate = rawData.reduce(
    (acc, rawItem) => {
      const date = parseISO(rawItem.date)
      const dateStr = rawItem.date.split('T')[0]

      let type
      if (date < now || rawItem.allDay) {
        type = PastNextFuture.Past
      } else if (alreadyFuture) {
        type = PastNextFuture.Future
      } else {
        type = PastNextFuture.Next
        alreadyFuture = true
      }

      if (date < dateFrom) {
        return acc
      }

      const entry: EconomicEntry = {
        ...rawItem,
        parsedDate: date,
        type,
        isOpen: rawItem.calendarId === openedDetail && !rawItem.hasNoDetail,
        alert: alertsState?.hasOwnProperty(rawItem.ticker) ? alertsState[rawItem.ticker] : rawItem.alert,
      }

      if (dateStr in acc) {
        acc[dateStr] = [...acc[dateStr], entry]
      } else {
        acc[dateStr] = [entry]
      }
      return acc
    },
    {} as { [key: string]: EconomicEntry[] }
  )

  const endDate = addDays(dateFrom, 4)
  const allWeekdays = eachDayOfInterval({ start: dateFrom, end: endDate })
  const days = allWeekdays.map((currentDate) => {
    const formattedDate = format(currentDate, ISO_DATEONLY_FORMAT)
    return (
      eventsByDate[formattedDate] ?? [{ date: formattedDate, parsedDate: currentDate, isEmpty: true } as EconomicEntry]
    )
  })

  return {
    days,
  }
}

export function processEarningsCalendarData(rawData: EarningsEntryRaw[]): EarningsEntry[] {
  return rawData.map((entry) => ({
    ...entry,
    parsedEarningsDate: entry.earningsDate ? parseISO(entry.earningsDate) : undefined,
  }))
}

export function processDividendsCalendarData(rawData: DividendsEntryRaw[]): DividendsEntry[] {
  return rawData.map((entry) => ({
    ...entry,
    parsedExDate: parseISO(entry.exdate),
  }))
}

export function getCalendarTradingDay() {
  const nowNYC = getDate()
  const startNowNYC = startOfWeek(nowNYC, { weekStartsOn: 1 })

  if (isWeekend(nowNYC)) {
    return addDays(startNowNYC, 4)
  }

  return startOfDay(nowNYC)
}

export function isChevronVisible(entry: EconomicEntry) {
  return !entry.hasNoDetail && !entry.allDay
}
